import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Rocket, CheckCircle2, Circle, Copy, PartyPopper, ArrowRight, Sparkles } from "lucide-react";
import AppShell from "@/components/AppShell";
import { FullPageLoader } from "@/components/Common";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function LaunchCenter() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [announcement, setAnnouncement] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/launch").then(({ data }) => {
      setData(data);
      setAnnouncement(data.announcement || "");
    }).catch(() => setData({ error: true }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const markLaunched = async () => {
    setBusy(true);
    try {
      await api.post("/launch/mark");
      toast.success("Your business is launched. Quietly proud of you.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not mark as launched.");
    } finally { setBusy(false); }
  };

  const recordSale = async () => {
    setBusy(true);
    try {
      await api.post("/launch/first-sale");
      toast.success("First sale recorded. This is the whole point. Well done.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not record the sale.");
    } finally { setBusy(false); }
  };

  const copyAnnouncement = async () => {
    try {
      await navigator.clipboard?.writeText(announcement);
      toast.success("Announcement copied. Paste it where your customers are.");
    } catch {
      toast.error("Couldn't copy automatically. Select the text and copy it manually.");
    }
  };

  if (!data) return <FullPageLoader label="Opening Launch Center…" />;
  if (data.error) return <AppShell><div className="p-10 text-slate-600">Something went wrong.</div></AppShell>;

  const done = data.checklist.filter((c) => c.done).length;
  const remaining = data.checklist.filter((c) => !c.done);

  return (
    <AppShell>
      <div className="thin-scroll mx-auto max-w-3xl px-8 py-10 ltifade" data-testid="launch-center">
        <div className="flex items-center gap-2">
          <Rocket size={20} className="text-brand" />
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Launch Center</h1>
        </div>
        <p className="mt-1 text-slate-500">Your finished assets, organized into a plan to go live.</p>

        {data.launched && (
          <div className="mt-6 rounded-2xl border-2 border-emerald-200 bg-emerald-50/50 p-6" data-testid="launched-banner">
            <PartyPopper size={22} className="text-emerald-500" />
            <h2 className="mt-2 font-heading text-xl font-semibold">You're live.</h2>
            <p className="mt-1 text-slate-600">{data.business.name} is launched. Now the real work: getting it in front of people.</p>
            {data.first_sale ? (
              <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-sm font-medium text-emerald-600">
                <CheckCircle2 size={15} /> First sale recorded
              </p>
            ) : (
              <button data-testid="record-sale-btn" onClick={recordSale} disabled={busy} className="mt-4 rounded-lg bg-brand px-5 py-2.5 text-sm font-medium text-white transition-all duration-200 hover:bg-brand-hover disabled:opacity-50">
                Record my first sale
              </button>
            )}
          </div>
        )}

        {/* readiness */}
        <div className="mt-6 rounded-xl border border-brand-line bg-white p-5 shadow-soft">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">Launch readiness</p>
            <p className="font-heading text-lg font-semibold">{data.readiness?.launch_readiness ?? 0}%</p>
          </div>
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-brand-surface">
            <div className="h-full rounded-full bg-brand transition-all duration-500" style={{ width: `${data.readiness?.launch_readiness ?? 0}%` }} />
          </div>
          <p className="mt-2 text-xs text-slate-400">{done} of {data.checklist.length} assets built</p>
        </div>

        {/* checklist */}
        <h3 className="mt-8 font-heading text-lg font-medium">Your launch checklist</h3>
        <div className="mt-3 overflow-hidden rounded-xl border border-brand-line bg-white shadow-soft" data-testid="launch-checklist">
          {data.checklist.map((c) => (
            <div key={c.slug} className="flex items-center gap-3 border-b border-brand-line px-4 py-3 last:border-0">
              {c.done ? <CheckCircle2 size={18} className="text-emerald-500" /> : <Circle size={18} className="text-brand-muted" />}
              <span className="flex-1 text-sm">
                <span className={c.done ? "" : "text-slate-500"}>{c.title}</span>
                <span className="ml-2 text-xs text-slate-400">{c.asset_name}</span>
              </span>
              {!c.done && (
                <button onClick={() => navigate(`/task/${c.slug}`)} className="text-xs font-medium text-brand-text hover:underline">
                  Finish
                </button>
              )}
            </div>
          ))}
        </div>

        {!data.ready_to_launch && (
          <div className="mt-4 rounded-xl border border-brand-line bg-brand-surface/40 px-4 py-3 text-sm text-slate-600">
            {remaining.length} {remaining.length === 1 ? "step" : "steps"} to go before you can launch. Your next one:{" "}
            <button onClick={() => navigate(`/task/${remaining[0].slug}`)} className="font-medium text-brand-text hover:underline">
              {remaining[0].title}
            </button>
          </div>
        )}

        {/* announcement */}
        <h3 className="mt-8 font-heading text-lg font-medium">Your launch announcement</h3>
        <p className="mt-1 text-sm text-slate-500">
          {announcement ? "Pulled from your Launch Kit. Edit it, then copy it wherever your customers are." : "Finish the last step to draft your announcement, or write one here."}
        </p>
        <textarea
          data-testid="announcement-input"
          value={announcement}
          onChange={(e) => setAnnouncement(e.target.value)}
          rows={5}
          placeholder="The short, honest message you'll post when you go live."
          className="mt-3 w-full resize-y rounded-xl border border-brand-line bg-white p-3.5 text-sm leading-relaxed shadow-soft focus:border-brand focus:outline-none"
        />
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button data-testid="copy-announcement-btn" onClick={copyAnnouncement} disabled={!announcement.trim()} className="inline-flex items-center gap-2 rounded-lg border border-brand-line px-4 py-2 text-sm font-medium text-brand-text transition-all duration-200 hover:bg-brand-surface disabled:opacity-40">
            <Copy size={15} /> Copy announcement
          </button>
          {!announcement && (
            <button onClick={() => navigate("/task/build-brand")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
              <Sparkles size={14} /> Draft it in the last step
            </button>
          )}
        </div>

        {/* launch action */}
        {!data.launched && (
          <div className="mt-8 border-t border-brand-line pt-6">
            <button
              data-testid="mark-launched-btn"
              onClick={markLaunched}
              disabled={!data.ready_to_launch || busy}
              className="inline-flex items-center gap-2 rounded-lg bg-brand px-6 py-3 text-sm font-medium text-white transition-all duration-200 hover:bg-brand-hover hover:shadow-lift disabled:opacity-40"
            >
              <Rocket size={16} /> Mark my business as launched
            </button>
            {!data.ready_to_launch && <p className="mt-2 text-xs text-slate-400">Available once all seven assets are built.</p>}
          </div>
        )}

        <button onClick={() => navigate("/business")} className="mt-8 inline-flex items-center gap-1.5 text-sm font-medium text-brand-text hover:underline">
          Review or export your business <ArrowRight size={15} />
        </button>
      </div>
    </AppShell>
  );
}
